package one.devsbox.shared.msg

import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalTime
import kotlinx.datetime.isoDayNumber
import kotlinx.serialization.Serializable
import kotlinx.serialization.decodeFromByteArray
import kotlinx.serialization.encodeToByteArray
import kotlinx.serialization.protobuf.ProtoNumber
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds


@Serializable
sealed interface MessageBody : Message {

    companion object {

        @Suppress("UNCHECKED_CAST")
        fun <T : MessageBody> decode(data: ByteArray?): T {
            return PROTO_INSTANCE_MSG.decodeFromByteArray<MessageBody>(data!!) as T
        }

        fun encode(data: MessageBody): ByteArray {
            return PROTO_INSTANCE_MSG.encodeToByteArray<MessageBody>(data)
        }
    }

    @Serializable
    data class Alive(
        @ProtoNumber(1)
        var alive: Boolean
    ) : MessageBody {
        companion object {}
    }

    @Serializable
    data class Cmd(
        @ProtoNumber(1)
        var ok: Boolean,
        @ProtoNumber(2)
        var msg: List<String> = emptyList(),
        @ProtoNumber(3)
        var out: List<String> = emptyList(),
        @ProtoNumber(4)
        var err: List<String> = emptyList(),
        @ProtoNumber(5)
        var duration: Duration = 0.seconds,
    ) : MessageBody {
        companion object {}
    }

    @Serializable
    data class Monitor(
        @ProtoNumber(1)
        var online: Boolean,
        @ProtoNumber(2)
        var kbps: Long,
        @ProtoNumber(3)
        var ping: Long,
        @ProtoNumber(4)
        var vRuntime: Int,
        @ProtoNumber(5)
        var vWWW: Int,
        @ProtoNumber(6)
        var data: String
    ) : MessageBody {
        companion object {}
    }

    @Serializable
    data class Log(
        @ProtoNumber(1)
        var type: Int,
        @ProtoNumber(2)
        var data: String
    ) : MessageBody {
        companion object {}
    }

    @Serializable
    data class Config(
        @ProtoNumber(1)
        var deviceId: Long = -1L,
        @ProtoNumber(2)
        var applicationId: Long = -1,
        @ProtoNumber(3)
        var screenW: Int = 1920,
        @ProtoNumber(4)
        var screenH: Int = 1080,
        @ProtoNumber(5)
        var screenR: Int = 2,
        @ProtoNumber(6)
        var openingHours: List<OpeningHour> = OpeningHour.all(),
        @ProtoNumber(7)
        var runtimeId: Long = -1,
    ) : MessageBody {
        companion object {}

        @Serializable
        data class OpeningHour(
            val dow: Int = DayOfWeek.MONDAY.isoDayNumber,
            val from: LocalTime = LocalTime(6, 0),
            val to: LocalTime = LocalTime(22, 0)
        ) {
            companion object {

                /**
                 * Rounds a LocalTime to the nearest minute.
                 * seconds >= 30 → round up (e.g. 01:59:59 → 02:00)
                 * seconds <  30 → truncate  (e.g. 01:00:29 → 01:00)
                 * Handles hour overflow (e.g. 23:59:45 → 00:00 of next virtual day — avoid by using
                 * the endOfDay constant directly instead of rounding 23:59:xx).
                 */
                private fun LocalTime.roundToMinute(): LocalTime {
                    val roundedMinute = if (second >= 30) minute + 1 else minute
                    return when {
                        roundedMinute >= 60 && hour == 23 -> LocalTime(23, 59, 0) // cap: don't overflow to 00:00
                        roundedMinute >= 60 -> LocalTime(hour + 1, 0, 0)
                        else -> LocalTime(hour, roundedMinute, 0)
                    }
                }

                /**
                 * Converts a list of OpeningHours from UTC to local time by adding [offset].
                 * If the shifted range crosses midnight, the entry is split into two:
                 *   e.g. UTC 20:00-23:59 + 2h → [22:00-23:59 (same day), 00:00-02:00 (next day)]
                 */
                fun fromUTC(items: List<OpeningHour>, offset: Duration): List<OpeningHour> {
                    val secondsInDay = 86400L
                    val endOfDay = LocalTime(23, 59, 0)
                    val startOfDay = LocalTime(0, 0, 0)
                    val result = mutableListOf<OpeningHour>()
                    for (hour in items) {
                        val fromSec = hour.from.toSecondOfDay() + offset.inWholeSeconds
                        val toSec = hour.to.toSecondOfDay() + offset.inWholeSeconds
                        val fromDayShift = fromSec.floorDiv(secondsInDay).toInt()
                        val toDayShift = toSec.floorDiv(secondsInDay).toInt()
                        val newFrom = LocalTime.fromSecondOfDay(fromSec.mod(secondsInDay).toInt()).roundToMinute()
                        val newTo = LocalTime.fromSecondOfDay(toSec.mod(secondsInDay).toInt()).roundToMinute()
                        if (fromDayShift == toDayShift) {
                            // No midnight crossing — single entry
                            val newDow = ((hour.dow - 1 + fromDayShift).mod(7) + 7).mod(7) + 1
                            result.add(OpeningHour(dow = newDow, from = newFrom, to = newTo))
                        } else {
                            // Crosses midnight — split into two entries
                            val dowFirst = ((hour.dow - 1 + fromDayShift).mod(7) + 7).mod(7) + 1
                            val dowSecond = ((hour.dow - 1 + toDayShift).mod(7) + 7).mod(7) + 1
                            result.add(OpeningHour(dow = dowFirst, from = newFrom, to = endOfDay))
                            // Skip second entry if to lands exactly on midnight (00:00 → 00:00 is invalid)
                            if (newTo != startOfDay) {
                                result.add(OpeningHour(dow = dowSecond, from = startOfDay, to = newTo))
                            }
                        }
                    }
                    return result
                }

                /**
                 * Converts a list of OpeningHours from local time to UTC by subtracting [offset].
                 * If the shifted range crosses midnight, the entry is split into two:
                 *   e.g. local 01:00-05:00 - 2h → [23:00-23:59 (prev day), 00:00-03:00 (same day)]
                 */
                fun toUTC(items: List<OpeningHour>, offset: Duration): List<OpeningHour> {
                    val secondsInDay = 86400L
                    val endOfDay = LocalTime(23, 59, 0)
                    val startOfDay = LocalTime(0, 0, 0)
                    val result = mutableListOf<OpeningHour>()
                    for (hour in items) {
                        val fromSec = hour.from.toSecondOfDay() - offset.inWholeSeconds
                        val toSec = hour.to.toSecondOfDay() - offset.inWholeSeconds
                        val fromDayShift = fromSec.floorDiv(secondsInDay).toInt()
                        val toDayShift = toSec.floorDiv(secondsInDay).toInt()
                        val newFrom = LocalTime.fromSecondOfDay(fromSec.mod(secondsInDay).toInt()).roundToMinute()
                        val newTo = LocalTime.fromSecondOfDay(toSec.mod(secondsInDay).toInt()).roundToMinute()
                        if (fromDayShift == toDayShift) {
                            // No midnight crossing — single entry
                            val newDow = ((hour.dow - 1 + fromDayShift).mod(7) + 7).mod(7) + 1
                            result.add(OpeningHour(dow = newDow, from = newFrom, to = newTo))
                        } else {
                            // Crosses midnight — split into two entries
                            val dowFirst = ((hour.dow - 1 + fromDayShift).mod(7) + 7).mod(7) + 1
                            val dowSecond = ((hour.dow - 1 + toDayShift).mod(7) + 7).mod(7) + 1
                            result.add(OpeningHour(dow = dowFirst, from = newFrom, to = endOfDay))
                            // Skip second entry if to lands exactly on midnight (00:00 → 00:00 is invalid)
                            if (newTo != startOfDay) {
                                result.add(OpeningHour(dow = dowSecond, from = startOfDay, to = newTo))
                            }
                        }
                    }
                    return result
                }

                fun all(
                    from: LocalTime = LocalTime(4, 0),
                    to: LocalTime = LocalTime(20, 0)
                ) = DayOfWeek.entries.map { OpeningHour(it.isoDayNumber, from, to) }
            }
        }
    }
}